import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Warehouse, Rows3, LayoutGrid, Layers, BoxSelect, Upload, Download, Edit2, Check, X, Tag, Settings, Lock, Unlock, ShieldAlert, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const LocationSetup = () => {
  const navigate = useNavigate();
  const [storageType, setStorageType] = useState('SFG');
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedHalls, setExpandedHalls] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRacks, setExpandedRacks] = useState({});
  const [expandedLevels, setExpandedLevels] = useState({});
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'hall', 'row', 'rack', 'level', 'bin'
  const [dialogParentId, setDialogParentId] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemCapacity, setNewItemCapacity] = useState('');
  
  // Import states
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const fileInputRef = useRef(null);

  // Repair hidden/orphaned bins state
  const [repairing, setRepairing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  
  // Edit capacity state
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [editCapacityValue, setEditCapacityValue] = useState('');
  
  // Category configuration for halls
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedHall, setSelectedHall] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // ── Bin Lock / Release ──────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('hierarchy'); // 'hierarchy' | 'summary'
  const [locksByNode, setLocksByNode] = useState({});       // node_id -> lock
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockTarget, setLockTarget] = useState(null);       // {scope, node_id, label}
  const [lockPutAway, setLockPutAway] = useState(false);
  const [lockPicking, setLockPicking] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [savingLock, setSavingLock] = useState(false);

  const fetchLocks = async () => {
    try {
      const res = await apiClient.get(`/wms/bin-locks?storage_type=${storageType}`);
      const map = {};
      (res.data.locks || []).forEach((l) => { map[l.node_id] = l; });
      setLocksByNode(map);
    } catch (e) {
      setLocksByNode({});
    }
  };

  const fetchSummary = async () => {
    try {
      setSummaryLoading(true);
      const res = await apiClient.get(`/wms/bin-locks/summary?storage_type=${storageType}`);
      setSummary(res.data.summary || []);
    } catch (e) {
      toast.error('Failed to load locked bins summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'summary') fetchSummary();
  }, [activeTab, storageType]);

  const openLockDialog = (scope, nodeId, label) => {
    const existing = locksByNode[nodeId];
    setLockTarget({ scope, node_id: nodeId, label });
    setLockPutAway(existing?.block_put_away || false);
    setLockPicking(existing?.block_picking || false);
    setLockReason(existing?.reason || '');
    setLockDialogOpen(true);
  };

  const saveLock = async () => {
    if (!lockTarget) return;
    try {
      setSavingLock(true);
      await apiClient.post('/wms/bin-locks/set', {
        storage_type: storageType,
        scope: lockTarget.scope,
        node_id: lockTarget.node_id,
        block_put_away: lockPutAway,
        block_picking: lockPicking,
        reason: lockReason,
      });
      const released = !(lockPutAway || lockPicking);
      toast.success(released ? `Released ${lockTarget.label}` : `Updated lock on ${lockTarget.label}`);
      setLockDialogOpen(false);
      fetchLocks();
      if (activeTab === 'summary') fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update lock');
    } finally {
      setSavingLock(false);
    }
  };

  // Resolve the effective lock for a node from its hierarchy code path (label).
  // A lock cascades DOWN: a child is locked when an ancestor's code_prefix is a
  // prefix of the child's path. A direct lock on the node itself takes priority.
  const getEffectiveLock = (nodeId, path) => {
    const direct = locksByNode[nodeId];
    if (direct && (direct.block_put_away || direct.block_picking)) {
      return { lock: direct, inherited: false };
    }
    for (const l of Object.values(locksByNode)) {
      if (!l || !(l.block_put_away || l.block_picking)) continue;
      const p = l.code_prefix;
      if (!p || !path) continue;
      if (path === p || path.startsWith(p + '-')) {
        return { lock: l, inherited: true };
      }
    }
    return null;
  };

  // Small lock badge + toggle button rendered on each hierarchy node.
  const LockControl = ({ scope, nodeId, label }) => {
    const eff = getEffectiveLock(nodeId, label);
    const lock = eff?.lock;
    const inherited = eff?.inherited;
    const isLocked = !!lock;
    let title = 'Lock this location for put-away / picking';
    if (isLocked) {
      const parts = [];
      if (lock.block_put_away) parts.push('Put-Away');
      if (lock.block_picking) parts.push('Picking');
      title = inherited
        ? `LOCKED via parent "${lock.node_label || lock.code_prefix}": ${parts.join(' + ')}${lock.reason ? ` — ${lock.reason}` : ''}. Release it on the parent node.`
        : `LOCKED: ${parts.join(' + ')}${lock.reason ? ` — ${lock.reason}` : ''}`;
    }
    const badgeCls = inherited
      ? 'bg-amber-100 text-amber-700'
      : 'bg-red-100 text-red-700';
    return (
      <div className="flex items-center gap-1">
        {isLocked && (
          <span className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${badgeCls}`} title={title} data-testid={`lock-badge-${nodeId}`}>
            <ShieldAlert className="w-3 h-3" />
            {lock.block_put_away ? 'PA' : ''}{lock.block_put_away && lock.block_picking ? '+' : ''}{lock.block_picking ? 'PK' : ''}
            {inherited ? ' ↧' : ''}
          </span>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); openLockDialog(scope, nodeId, label); }}
          className={isLocked ? (inherited ? 'text-amber-600 hover:text-amber-700' : 'text-red-600 hover:text-red-700') : 'text-gray-500 hover:text-gray-700'}
          title={title}
          data-testid={`lock-btn-${nodeId}`}
        >
          {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    fetchHierarchy();
    fetchLocks();
  }, [storageType]);

  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/wms/location-hierarchy?storage_type=${storageType}`);
      setHierarchy(response.data.hierarchy || []);
    } catch (error) {
      toast.error('Failed to fetch location hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, parentId = '') => {
    setDialogType(type);
    setDialogParentId(parentId);
    setNewItemName('');
    setNewItemCapacity('');
    setDialogOpen(true);
  };
  
  // Import functionality
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    setImportProgress({ phase: 'starting', processed: 0, total: 0 });
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/wms/locations/import?storage_type=${storageType}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          },
          body: formData
        }
      );

      // 2026-05-21 — Same defensive read pattern as PerSectionLocationSetup
      // (service workers on mwwms.com were locking the body, throwing
      // "ReadableStreamDefaultReader constructor can only accept readable
      // streams that are not yet locked to a reader"). Falls back to a
      // single text() parse when streaming is unavailable.
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        toast.error(`Import failed (${response.status}): ${errText || response.statusText}`);
        return;
      }

      let reader;
      try {
        const streamSource = (response.body && !response.body.locked) ? response : response.clone();
        reader = streamSource.body.getReader();
      } catch {
        reader = null;
      }
      const decoder = new TextDecoder();

      const parseSseChunk = (text) => {
        const lines = text.split('\n').filter(line => line.startsWith('data:'));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.replace('data: ', ''));
            setImportProgress(data);
            if (data.error) {
              toast.error(data.error);
              setImporting(false);
              return;
            }
            if (data.phase === 'completed') {
              toast.success(`Import complete! Created: ${data.created}, Updated: ${data.updated}`);
              if (data.total_errors > 0) toast.warning(`${data.total_errors} errors occurred`);
              fetchHierarchy();
            }
          } catch (e) {
            // Ignore parsing errors for incomplete chunks
          }
        }
      };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          parseSseChunk(decoder.decode(value));
        }
      } else {
        const fullText = await response.clone().text();
        parseSseChunk(fullText);
      }
    } catch (error) {
      toast.error('Import failed: ' + error.message);
    } finally {
      setImporting(false);
      setImportProgress(null);
      event.target.value = '';
    }
  };
  
  const handleExportTemplate = async () => {
    try {
      const response = await apiClient.get(`/wms/locations/export-template?storage_type=${storageType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wms_locations_${storageType}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  // Edit capacity functions
  const startEditCapacity = (levelId, currentCapacity) => {
    setEditingLevelId(levelId);
    setEditCapacityValue(currentCapacity?.toString() || '0');
  };
  
  const cancelEditCapacity = () => {
    setEditingLevelId(null);
    setEditCapacityValue('');
  };
  
  const saveCapacity = async (levelId) => {
    try {
      const capacity = parseInt(editCapacityValue) || 0;
      await apiClient.patch(`/wms/levels/${levelId}/capacity?capacity=${capacity}`);
      toast.success('Capacity updated');
      setEditingLevelId(null);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update capacity');
    }
  };

  // Category Management Functions
  const fetchAvailableCategories = async () => {
    try {
      const response = await apiClient.get('/wms/category4-values');
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const openCategoryDialog = (hall) => {
    setSelectedHall(hall);
    setSelectedCategories(hall.categories || []);
    fetchAvailableCategories();
    setCategoryDialogOpen(true);
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const saveHallCategories = async () => {
    if (!selectedHall) return;
    
    try {
      await apiClient.patch(`/wms/locations/${selectedHall.location_id}/categories`, {
        categories: selectedCategories
      });
      toast.success('Hall categories updated');
      setCategoryDialogOpen(false);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update categories');
    }
  };

  // SKU Assignment Functions
  const handleRepairHiddenBins = async () => {
    setRepairing(true);
    try {
      // 1) Dry-run preview
      const preview = await apiClient.post('/wms/admin/repair-orphan-storage-locations', {
        dry_run: true, storage_type: storageType,
      });
      const d = preview.data;
      const totalFix = (d.halls_to_create || 0) + (d.halls_to_retag || 0);
      if (!d.orphan_levels_found || totalFix === 0) {
        toast.success('No hidden/orphaned bins found for this storage type 🎉');
        return;
      }
      const names = [...(d.new_hall_names || []), ...(d.retagged_hall_names || [])].join(', ');
      const ok = window.confirm(
        `Found ${d.orphan_levels_found} bin(s) hidden from the hierarchy.\n\n` +
        `This will restore ${d.halls_to_create} hall(s), retag ${d.halls_to_retag} hall(s), ` +
        `and rebuild ${d.rows_to_create} row(s) + ${d.racks_to_create} rack(s).\n` +
        `Halls: ${names}\n\nNo inventory/put-away data is changed. Proceed?`
      );
      if (!ok) return;

      // 2) Apply
      const res = await apiClient.post('/wms/admin/repair-orphan-storage-locations', {
        dry_run: false, storage_type: storageType,
      });
      const a = res.data;
      toast.success(
        `Restored ${a.halls_to_create} hall(s) + retagged ${a.halls_to_retag}. Your bins are now visible.`
      );
      fetchHierarchy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const handleResyncBinStatus = async () => {
    setResyncing(true);
    try {
      const res = await apiClient.post(`/wms/bin-locks/resync-inventory-status?storage_type=${storageType}`);
      const d = res.data || {};
      toast.success(
        `Resynced ${storageType} bin status: ${d.active_locks || 0} active lock(s), ${(d.inventory_rows_updated || 0).toLocaleString()} stock row(s) updated.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resync failed');
    } finally {
      setResyncing(false);
    }
  };


  const handleCreate = async () => {
    if (!newItemName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      let endpoint = '';
      let payload = {};

      switch (dialogType) {
        case 'hall':
          endpoint = '/wms/locations';
          payload = { location_name: newItemName, storage_type: storageType };
          break;
        case 'row':
          endpoint = '/wms/rows';
          payload = { location_id: dialogParentId, row_name: newItemName };
          break;
        case 'rack':
          endpoint = '/wms/racks';
          payload = { row_id: dialogParentId, rack_name: newItemName };
          break;
        case 'level':
          endpoint = '/wms/levels';
          payload = { 
            rack_id: dialogParentId, 
            level_name: newItemName,
            capacity: parseInt(newItemCapacity) || 0
          };
          break;
        case 'bin':
          endpoint = '/wms/bins';
          payload = { 
            level_id: dialogParentId, 
            bin_name: newItemName,
            capacity: parseInt(newItemCapacity) || 100
          };
          break;
        default:
          return;
      }

      await apiClient.post(endpoint, payload);
      toast.success(`${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)} created successfully`);
      setDialogOpen(false);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to create ${dialogType}`);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}? This will also delete all children.`)) {
      return;
    }

    try {
      // Map 'hall' to 'location' for API
      const apiType = type === 'hall' ? 'location' : type;
      await apiClient.delete(`/wms/${apiType}s/${id}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
    }
  };

  const toggleHall = (hallId) => {
    setExpandedHalls(prev => ({ ...prev, [hallId]: !prev[hallId] }));
  };

  const toggleRow = (rowId) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const toggleRack = (rackId) => {
    setExpandedRacks(prev => ({ ...prev, [rackId]: !prev[rackId] }));
  };

  const toggleLevel = (levelId) => {
    setExpandedLevels(prev => ({ ...prev, [levelId]: !prev[levelId] }));
  };

  const getDialogTitle = () => {
    const titles = {
      hall: 'Add New Hall',
      row: 'Add New Row',
      rack: 'Add New Rack',
      level: storageType === 'SFG' ? 'Add New Level (Final for SFG)' : 'Add New Level',
      bin: 'Add New Bin (FG Only)'
    };
    return titles[dialogType] || 'Add New Item';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/wms')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">WMS Location Setup</h1>
            <p className="text-sm text-[#E8DCC8]">Configure storage locations hierarchy</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Storage Type Selector & Actions */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Label className="text-[#1A1F2E] font-medium">Storage Type:</Label>
              <Select value={storageType} onValueChange={setStorageType}>
                <SelectTrigger className="w-40" data-testid="storage-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SFG">SFG Storage</SelectItem>
                  <SelectItem value="FG">FG Storage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={handleExportTemplate}
                className="text-[#2D5F4F] border-[#2D5F4F]"
                data-testid="export-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Template
              </Button>
              <Button
                onClick={handleImportClick}
                disabled={importing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="import-locations-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importing...' : 'Import Locations'}
              </Button>
              <Button
                onClick={handleRepairHiddenBins}
                disabled={repairing}
                variant="outline"
                className="text-amber-700 border-amber-400 hover:bg-amber-50"
                data-testid="repair-hidden-bins-btn"
                title="Restore bins that exist (and accept put-away) but are missing from this hierarchy view"
              >
                <Settings className={`w-4 h-4 mr-2 ${repairing ? 'animate-spin' : ''}`} />
                {repairing ? 'Checking...' : 'Repair Hidden Bins'}
              </Button>
              <Button
                onClick={() => openDialog('hall')}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                data-testid="add-location-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Hall
              </Button>
            </div>
          </div>
          
          {/* Import Progress */}
          {importProgress && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                {importProgress.phase === 'starting' && 'Starting import...'}
                {importProgress.phase === 'processing' && `Processing: ${importProgress.processed}/${importProgress.total}`}
                {importProgress.phase === 'completed' && `✓ Complete! Created: ${importProgress.created}, Updated: ${importProgress.updated}`}
              </p>
              {importProgress.phase === 'processing' && (
                <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-300" 
                    style={{ width: `${(importProgress.processed / importProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Import Format Info */}
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>CSV Format:</strong> hall_name, row_name, rack_name, level_name, capacity, velocity_class
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Example: H01, A, 01, A, 100, C → Creates bin code H01-A-01-A with capacity 100
            </p>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex items-center gap-2 mb-4" data-testid="location-setup-tabs">
          <button
            onClick={() => setActiveTab('hierarchy')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'hierarchy' ? 'bg-[#2D5F4F] text-white' : 'bg-white border border-[#E5DFD0] text-[#5A5F6E] hover:bg-gray-50'}`}
            data-testid="tab-hierarchy"
          >
            <LayoutGrid className="w-4 h-4 inline mr-2" />
            Hierarchy
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'summary' ? 'bg-[#2D5F4F] text-white' : 'bg-white border border-[#E5DFD0] text-[#5A5F6E] hover:bg-gray-50'}`}
            data-testid="tab-summary"
          >
            <ListChecks className="w-4 h-4 inline mr-2" />
            Locked Bins Summary
          </button>
        </div>

        {/* Locked Bins Summary */}
        {activeTab === 'summary' && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6" data-testid="locked-bins-summary">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-lg font-semibold text-[#1A1F2E] font-display">
                {storageType} Locked Bins Summary
              </h2>
              <Button
                variant="outline"
                size="sm"
                disabled={resyncing}
                onClick={handleResyncBinStatus}
                data-testid="resync-bin-status-btn"
              >
                <ListChecks className="w-4 h-4 mr-1.5" />
                {resyncing ? 'Resyncing…' : 'Resync Bin Status'}
              </Button>
            </div>
            <p className="text-xs text-[#5A5F6E] -mt-2 mb-4">
              "Resync Bin Status" stamps the current lock state (locked / put-away / picking) onto every stock row in {storageType} inventory,
              so the "Bin Status" column in the {storageType} Inventory Report reflects existing locks. New locks/releases update automatically —
              run this once for locks that already existed.
            </p>
            {summaryLoading ? (
              <div className="text-center py-8 text-[#5A5F6E]">Loading...</div>
            ) : summary.length === 0 ? (
              <div className="text-center py-12">
                <Unlock className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
                <p className="text-[#5A5F6E]">No locked bins for {storageType} storage.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#5A5F6E] border-b border-[#E5DFD0]">
                      <th className="py-2 pr-4">Locked Node</th>
                      <th className="py-2 pr-4">Scope</th>
                      <th className="py-2 pr-4">Blocked</th>
                      <th className="py-2 pr-4">Affected Bins</th>
                      <th className="py-2 pr-4">Stored Bundles</th>
                      <th className="py-2 pr-4">Locked Date</th>
                      <th className="py-2 pr-4">Locked By</th>
                      <th className="py-2 pr-4">Last Put-Away</th>
                      <th className="py-2 pr-4">Last Pick</th>
                      <th className="py-2 pr-4">Reason</th>
                      <th className="py-2 pr-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((r) => (
                      <tr key={r.lock_id} className="border-b border-[#F0EBE0]" data-testid={`summary-row-${r.lock_id}`}>
                        <td className="py-2 pr-4 font-mono font-medium text-[#1A1F2E]">{r.node_label}</td>
                        <td className="py-2 pr-4 capitalize">{r.scope}</td>
                        <td className="py-2 pr-4">
                          <div className="flex gap-1">
                            {r.block_put_away && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Put-Away</span>}
                            {r.block_picking && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Picking</span>}
                          </div>
                        </td>
                        <td className="py-2 pr-4">{r.affected_bins}</td>
                        <td className="py-2 pr-4">{r.stored_bundles}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{r.locked_at ? new Date(r.locked_at).toLocaleString() : '—'}</td>
                        <td className="py-2 pr-4">{r.locked_by || '—'}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{r.last_put_away_date ? new Date(r.last_put_away_date).toLocaleString() : '—'}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{r.last_pick_date ? new Date(r.last_pick_date).toLocaleString() : '—'}</td>
                        <td className="py-2 pr-4 max-w-[180px] truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                        <td className="py-2 pr-4">
                          <span className="text-xs text-[#5A5F6E]" data-testid={`summary-manage-${r.lock_id}`}>
                            Manage in Hierarchy tab
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-[#5A5F6E] mt-3">
                  Note: "Last Put-Away" and "Last Pick" reflect timestamps recorded on stock in the locked area. To release or change a lock, use the lock icon in the Hierarchy tab.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Location Hierarchy Tree */}
        {activeTab === 'hierarchy' && (
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
            {storageType} Storage Hierarchy
          </h2>

          {loading ? (
            <div className="text-center py-8 text-[#5A5F6E]">Loading...</div>
          ) : hierarchy.length === 0 ? (
            <div className="text-center py-12">
              <Warehouse className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No halls configured yet.</p>
              <p className="text-sm text-[#5A5F6E] mt-1">Click &quot;Add Hall&quot; to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {hierarchy.map((hall) => (
                <div key={hall.location_id} className="border border-[#E5DFD0] rounded-lg">
                  {/* Hall Level */}
                  <div 
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-t-lg cursor-pointer"
                    onClick={() => toggleHall(hall.location_id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {hall.rows?.length > 0 ? (
                        expandedHalls[hall.location_id] ? 
                          <ChevronDown className="w-4 h-4 text-blue-600" /> : 
                          <ChevronRight className="w-4 h-4 text-blue-600" />
                      ) : (
                        <div className="w-4" />
                      )}
                      <Warehouse className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-[#1A1F2E]">{hall.location_name}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Hall</span>
                      <span className="text-xs text-[#5A5F6E] ml-2">
                        ({hall.rows?.length || 0} rows)
                      </span>
                      {/* Display assigned categories */}
                      {hall.categories && hall.categories.length > 0 && (
                        <div className="flex items-center gap-1 ml-2">
                          <Tag className="w-3 h-3 text-purple-600" />
                          <span className="text-xs text-purple-700">
                            {hall.categories.slice(0, 3).join(', ')}
                            {hall.categories.length > 3 && ` +${hall.categories.length - 3} more`}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <LockControl scope="hall" nodeId={hall.location_id} label={hall.location_name} />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openCategoryDialog(hall); }}
                        className="text-purple-600 hover:text-purple-700"
                        title="Configure Categories"
                        data-testid={`configure-categories-${hall.location_id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openDialog('row', hall.location_id); }}
                        className="text-green-600 hover:text-green-700"
                        title="Add Row"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleDelete('hall', hall.location_id); }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Rows */}
                  {expandedHalls[hall.location_id] && hall.rows?.map((row) => (
                    <div key={row.row_id} className="ml-6 border-l-2 border-blue-200">
                      <div 
                        className="flex items-center justify-between p-2 bg-green-50 cursor-pointer"
                        onClick={() => toggleRow(row.row_id)}
                      >
                        <div className="flex items-center gap-2">
                          {row.racks?.length > 0 ? (
                            expandedRows[row.row_id] ? 
                              <ChevronDown className="w-4 h-4 text-green-600" /> : 
                              <ChevronRight className="w-4 h-4 text-green-600" />
                          ) : (
                            <div className="w-4" />
                          )}
                          <Rows3 className="w-4 h-4 text-green-600" />
                          <span className="text-[#1A1F2E]">{row.row_name}</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Row</span>
                          <span className="text-xs text-[#5A5F6E]">
                            ({row.racks?.length || 0} racks)
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <LockControl scope="row" nodeId={row.row_id} label={`${hall.location_name}-${row.row_name}`} />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openDialog('rack', row.row_id); }}
                            className="text-purple-600 hover:text-purple-700"
                            title="Add Rack"
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleDelete('row', row.row_id); }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Racks */}
                      {expandedRows[row.row_id] && row.racks?.map((rack) => (
                        <div key={rack.rack_id} className="ml-6 border-l-2 border-green-200">
                          <div 
                            className="flex items-center justify-between p-2 bg-purple-50 cursor-pointer"
                            onClick={() => toggleRack(rack.rack_id)}
                          >
                            <div className="flex items-center gap-2">
                              {rack.levels?.length > 0 ? (
                                expandedRacks[rack.rack_id] ? 
                                  <ChevronDown className="w-4 h-4 text-purple-600" /> : 
                                  <ChevronRight className="w-4 h-4 text-purple-600" />
                              ) : (
                                <div className="w-4" />
                              )}
                              <LayoutGrid className="w-4 h-4 text-purple-600" />
                              <span className="text-[#1A1F2E]">{rack.rack_name}</span>
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Rack</span>
                              <span className="text-xs text-[#5A5F6E]">
                                ({rack.levels?.length || 0} levels)
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <LockControl scope="rack" nodeId={rack.rack_id} label={`${hall.location_name}-${row.row_name}-${rack.rack_name}`} />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); openDialog('level', rack.rack_id); }}
                                className="text-orange-600 hover:text-orange-700"
                                title="Add Level"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleDelete('rack', rack.rack_id); }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>

                          {/* Levels */}
                          {expandedRacks[rack.rack_id] && rack.levels?.map((level) => (
                            <div key={level.level_id} className="ml-6 border-l-2 border-purple-200">
                              <div 
                                className={`flex items-center justify-between p-2 bg-orange-50 ${storageType === 'FG' ? 'cursor-pointer' : ''}`}
                                onClick={() => storageType === 'FG' && toggleLevel(level.level_id)}
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  {storageType === 'FG' && level.bins?.length > 0 ? (
                                    expandedLevels[level.level_id] ? 
                                      <ChevronDown className="w-4 h-4 text-orange-600" /> : 
                                      <ChevronRight className="w-4 h-4 text-orange-600" />
                                  ) : (
                                    <div className="w-4" />
                                  )}
                                  <Layers className="w-4 h-4 text-orange-600" />
                                  <span className="text-[#1A1F2E]">{level.level_name}</span>
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Level</span>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 rounded font-mono">
                                    {level.bin_code}
                                  </span>
                                  
                                  {/* Capacity Edit */}
                                  {editingLevelId === level.level_id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <span className="text-xs text-gray-500">Capacity:</span>
                                      <Input
                                        type="number"
                                        value={editCapacityValue}
                                        onChange={(e) => setEditCapacityValue(e.target.value)}
                                        className="w-20 h-6 text-xs"
                                        autoFocus
                                      />
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => saveCapacity(level.level_id)}
                                        className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                                      >
                                        <Check className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={cancelEditCapacity}
                                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div 
                                      className="flex items-center gap-1 cursor-pointer hover:bg-orange-100 px-2 py-0.5 rounded group"
                                      onClick={(e) => { e.stopPropagation(); startEditCapacity(level.level_id, level.capacity); }}
                                      title="Click to edit capacity"
                                    >
                                      <span className="text-xs text-gray-500">Capacity:</span>
                                      <span className="text-xs font-medium text-[#1A1F2E]">{level.capacity || 0}</span>
                                      <Edit2 className="w-3 h-3 text-gray-400 group-hover:text-orange-600" />
                                    </div>
                                  )}
                                  
                                  {/* Velocity Class Badge */}
                                  {level.velocity_class && (
                                    <span className={`text-xs px-2 py-0.5 rounded font-bold text-white ${
                                      level.velocity_class === 'A' ? 'bg-green-500' :
                                      level.velocity_class === 'B' ? 'bg-yellow-500' :
                                      'bg-red-500'
                                    }`}>
                                      {level.velocity_class}
                                    </span>
                                  )}
                                  
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    level.status === 'available' ? 'bg-green-100 text-green-700' :
                                    level.status === 'occupied' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {level.status}
                                  </span>
                                  
                                  {level.current_stock > 0 && (
                                    <span className="text-xs text-gray-500">
                                      Stock: {level.current_stock}
                                    </span>
                                  )}
                                  
                                  {storageType === 'FG' && (
                                    <span className="text-xs text-[#5A5F6E]">
                                      ({level.bins?.length || 0} bins)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <LockControl scope="level" nodeId={level.level_id} label={level.bin_code} />
                                  {storageType === 'FG' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => { e.stopPropagation(); openDialog('bin', level.level_id); }}
                                      className="text-cyan-600 hover:text-cyan-700"
                                      title="Add Bin"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleDelete('level', level.level_id); }}
                                    className="text-red-600 hover:text-red-700"
                                    disabled={level.current_stock > 0}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>

                              {/* Bins (FG only) */}
                              {storageType === 'FG' && expandedLevels[level.level_id] && level.bins?.map((bin) => (
                                <div key={bin.bin_id} className="ml-6 border-l-2 border-orange-200">
                                  <div className="flex items-center justify-between p-2 bg-cyan-50">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <BoxSelect className="w-4 h-4 text-cyan-600" />
                                      <span className="text-[#1A1F2E]">{bin.bin_name}</span>
                                      <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">Bin</span>
                                      <span className="text-xs px-2 py-0.5 bg-gray-100 rounded font-mono">
                                        {bin.bin_code}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        bin.status === 'available' || bin.status === 'empty' ? 'bg-green-100 text-green-700' :
                                        bin.status === 'occupied' || bin.status === 'full' ? 'bg-red-100 text-red-700' :
                                        'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {bin.status || 'empty'}
                                      </span>
                                      <span className="text-xs text-[#5A5F6E]">
                                        ({bin.current_qty || 0}/{bin.capacity || 100})
                                      </span>
                                      {/* Display current SKU in bin (auto-assigned during put-away) */}
                                      {bin.current_sku && (
                                        <div className="flex items-center gap-1 ml-2 bg-indigo-100 px-2 py-0.5 rounded">
                                          <Tag className="w-3 h-3 text-indigo-600" />
                                          <span className="text-xs text-indigo-700 font-medium">
                                            {bin.current_sku}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <LockControl scope="bin" nodeId={bin.bin_id} label={bin.bin_code} />
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDelete('bin', bin.bin_id)}
                                        className="text-red-600 hover:text-red-700"
                                        disabled={bin.current_qty > 0}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Legend */}
        {activeTab === 'hierarchy' && (
        <div className="mt-6 bg-white rounded-lg border border-[#E5DFD0] p-4">
          <h3 className="text-sm font-medium text-[#1A1F2E] mb-3">Hierarchy Legend</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded" />
              <span>Hall</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-50 border border-green-200 rounded" />
              <span>Row</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-50 border border-purple-200 rounded" />
              <span>Rack</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-50 border border-orange-200 rounded" />
              <span>Level {storageType === 'SFG' && '(Final)'}</span>
            </div>
            {storageType === 'FG' && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-cyan-50 border border-cyan-200 rounded" />
                <span>Bin (FG only)</span>
              </div>
            )}
          </div>
          <p className="text-xs text-[#5A5F6E] mt-3">
            {storageType === 'SFG' 
              ? 'SFG: Hall → Row → Rack → Level (bundles stored at level)' 
              : 'FG: Hall → Row → Rack → Level → Bin (individual packs stored in bins)'}
          </p>
        </div>
        )}
      </main>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={`Enter ${dialogType} name`}
                className="mt-1"
                data-testid="new-item-name-input"
              />
            </div>
            {(dialogType === 'level' || dialogType === 'bin') && (
              <div>
                <Label>Capacity {dialogType === 'bin' ? '(packs)' : '(optional)'}</Label>
                <Input
                  type="number"
                  value={newItemCapacity}
                  onChange={(e) => setNewItemCapacity(e.target.value)}
                  placeholder={dialogType === 'bin' ? 'Default: 100' : 'Enter capacity'}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="create-item-btn"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Configuration Dialog for Halls */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Categories for {selectedHall?.location_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#5A5F6E] mb-4">
              Select the Category 4 values that this hall should accept. Items will only be stored in this hall if they belong to one of these categories.
            </p>
            <div className="max-h-64 overflow-y-auto border rounded-lg p-3">
              {availableCategories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No categories found in item master</p>
              ) : (
                <div className="space-y-2">
                  {availableCategories.map((category) => (
                    <label 
                      key={category}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        selectedCategories.includes(category) ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-[#1A1F2E]">{category}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {selectedCategories.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-[#5A5F6E] mb-2">Selected ({selectedCategories.length}):</p>
                <div className="flex flex-wrap gap-1">
                  {selectedCategories.map((cat) => (
                    <span key={cat} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveHallCategories}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-categories-btn"
            >
              Save Categories
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bin Lock / Release Dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              Lock / Release — {lockTarget?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-[#5A5F6E]">
              Locking this <span className="font-medium capitalize">{lockTarget?.scope}</span> applies to it and all
              bins beneath it ({storageType} storage). Choose which transactions to block.
            </p>
            {(() => {
              const eff = lockTarget ? getEffectiveLock(lockTarget.node_id, lockTarget.label) : null;
              if (eff?.inherited) {
                return (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2.5" data-testid="lock-inherited-notice">
                    This location is already locked via its parent <span className="font-semibold">{eff.lock.node_label || eff.lock.code_prefix}</span>. It is enforced automatically — to release it, release the parent node.
                  </div>
                );
              }
              return null;
            })()}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-[#E5DFD0] cursor-pointer hover:bg-gray-50" data-testid="lock-toggle-putaway">
              <input
                type="checkbox"
                checked={lockPutAway}
                onChange={(e) => setLockPutAway(e.target.checked)}
                className="w-4 h-4 text-amber-600 rounded"
              />
              <div>
                <span className="text-sm font-medium text-[#1A1F2E]">Block Put-Away</span>
                <p className="text-xs text-[#5A5F6E]">No put-away, put-away-slip allocation, or Table Clearance placement here.</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-[#E5DFD0] cursor-pointer hover:bg-gray-50" data-testid="lock-toggle-picking">
              <input
                type="checkbox"
                checked={lockPicking}
                onChange={(e) => setLockPicking(e.target.checked)}
                className="w-4 h-4 text-red-600 rounded"
              />
              <div>
                <span className="text-sm font-medium text-[#1A1F2E]">Block Picking</span>
                <p className="text-xs text-[#5A5F6E]">Stock here won't be allocated or suggested in any pick slip.</p>
              </div>
            </label>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={lockReason}
                onChange={(e) => setLockReason(e.target.value)}
                placeholder="e.g. Stock audit, maintenance"
                className="mt-1"
                data-testid="lock-reason-input"
              />
            </div>
            {!(lockPutAway || lockPicking) && (
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                Both options off — saving will RELEASE this location.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveLock}
              disabled={savingLock}
              className={(lockPutAway || lockPicking) ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
              data-testid="save-lock-btn"
            >
              {savingLock ? 'Saving...' : (lockPutAway || lockPicking) ? 'Apply Lock' : 'Release'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationSetup;
